import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  DollarSign,
  LogOut,
  ChevronDown,
  ChevronUp,
  User,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { Logo } from "@/components/ui/logo";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useFavorites } from "@/hooks/useFavorites";
import { FavoritesSection } from "@/components/navigation/FavoritesSection";
import { FavoriteToggle } from "@/components/navigation/FavoriteToggle";
import type { FavoriteMenuItem } from "@/types/favorites";
import type { NavigationItem } from "@/components/layout/navigationItems";

export function MobileSidebar({ profile, user, navItems, location, onClose, logout }: any) {
  const [openSubmenus, setOpenSubmenus] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  // Favorites hook
  const {
    favorites,
    isLoading: favoritesLoading,
    isFavorite,
    toggleFavorite,
  } = useFavorites();

  // Helper function to convert NavigationItem to FavoriteMenuItem
  const toFavoriteMenuItem = (item: NavigationItem, category?: string): FavoriteMenuItem => {
    // Extract icon name from Lucide component
    const iconName = item.icon?.displayName || item.icon?.name || 'Circle';

    return {
      id: item.path || item.title,
      label: item.title,
      icon: iconName,
      path: item.path || '#',
      category,
      timestamp: Date.now(),
    };
  };

  const toggleSubmenu = (title: string) => {
    setOpenSubmenus(prev => ({ ...prev, [title]: !prev[title] }));
  };

  // Auto-open submenus based on current location
  useEffect(() => {
    const newOpenSubmenus: { [key: string]: boolean } = {};
    
    // Open Cadastros submenu if we're on any cadastros page
    if (location.pathname.startsWith('/settings/cadastros')) {
      newOpenSubmenus["Cadastros"] = true;
    }
    
    setOpenSubmenus(prev => ({ ...prev, ...newOpenSubmenus }));
  }, [location.pathname]);

  // Check if any submenu item is active
  const isSubmenuActive = (item: any): boolean => {
    if (!item.subItems) return false;
    return item.subItems.some((subItem: any) => location.pathname === subItem.path);
  };

  // Helper function to check if a route is active (same as DesktopSidebar)
  const isRouteActive = (path: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 flex items-center gap-2">
        <Logo variant="full" size="sm" />
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto"
          onClick={onClose}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 py-4 overflow-y-auto">
        {/* Favorites Section - Always at top */}
        <div className="px-2 mb-4">
          <FavoritesSection
            favorites={favorites}
            onItemClick={(item) => {
              navigate(item.path);
              onClose();
            }}
            isExpanded={true}
            maxVisibleItems={8}
          />
        </div>

        <div className="space-y-1 px-2">
          {navItems.map((item: any) => (
            <div key={item.path || item.title}>
              {item.subItems ? (
                <div className="space-y-1">
                  <div className="relative">
                    <button
                      onClick={() => toggleSubmenu(item.title)}
                      className={cn(
                        "flex items-center gap-3 w-full rounded-md px-3 py-2 text-sm transition-colors",
                        openSubmenus[item.title] || isSubmenuActive(item)
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <item.icon className="h-5 w-5" />
                          <span>{item.title}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {openSubmenus[item.title] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </div>
                    </button>
                    {/* Favorite Toggle - Always visible on mobile (44x44px touch target) */}
                    <div className="absolute right-8 top-1/2 -translate-y-1/2">
                      <FavoriteToggle
                        item={toFavoriteMenuItem(item)}
                        isFavorited={isFavorite(item.path || item.title)}
                        onToggle={(favItem) => toggleFavorite(favItem)}
                        size="md"
                      />
                    </div>
                  </div>

                  {openSubmenus[item.title] && (
                    <div className="ml-8 space-y-1">
                      {item.subItems.map((subItem: any) => (
                        <div key={subItem.path} className="relative">
                          <Link
                            to={subItem.path}
                            onClick={onClose}
                            className={cn(
                              "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                              isRouteActive(subItem.path)
                                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                : "hover:bg-accent hover:text-accent-foreground"
                            )}
                          >
                            <subItem.icon className="h-4 w-4" />
                            <span className="flex-1">{subItem.title}</span>
                          </Link>
                          {/* Favorite Toggle - Always visible on mobile */}
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <FavoriteToggle
                              item={toFavoriteMenuItem(subItem, item.title)}
                              isFavorited={isFavorite(subItem.path || subItem.title)}
                              onToggle={(favItem) => toggleFavorite(favItem)}
                              size="sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors pr-12",
                      isRouteActive(item.path)
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="flex-1">{item.title}</span>
                  </Link>
                  {/* Favorite Toggle - Always visible on mobile */}
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <FavoriteToggle
                      item={toFavoriteMenuItem(item)}
                      isFavorited={isFavorite(item.path || item.title)}
                      onToggle={(favItem) => toggleFavorite(favItem)}
                      size="md"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 border-t">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full p-2 h-auto hover:bg-accent hover:text-accent-foreground transition-all duration-200"
            >
              <div className="flex items-center gap-3 w-full">
                <Avatar>
                  <AvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-semibold">
                    {profile?.full_name?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium leading-none">{profile?.full_name || 'Usuário'}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email || 'usuario@exemplo.com'}
                  </p>
                </div>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-64 dropdown-content animate-slide-down">
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-accent/5 rounded-t-lg">
              <Avatar className="h-10 w-10">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-semibold">
                  {profile?.full_name?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <p className="text-sm font-semibold leading-none">{profile?.full_name || 'Usuário'}</p>
                <p className="text-xs text-muted-foreground mt-1">{user?.email}</p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => navigate('/settings/profile')}
              className="cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <User className="mr-3 h-4 w-4" />
              Meu Perfil
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate('/settings')}
              className="cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Settings className="mr-3 h-4 w-4" />
              Configurações
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={async () => {
                try {
                  await authLogout();
                  toast.success('Logout realizado com sucesso!');
                } catch (error) {
                  toast.error('Erro ao fazer logout');
                }
              }}
              className="cursor-pointer hover:bg-destructive/10 hover:text-destructive text-destructive focus:text-destructive transition-colors"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
